import { useTheme } from "next-themes";

import baseBrand from "@/config/brand";
import { useBranding } from "@/hooks/useBranding";
import { cn } from "@/lib/utils";

type BrandLogoProps = {
  showName?: boolean;
  showSlogan?: boolean;
  stacked?: boolean;
  className?: string;
  imageClassName?: string;
  textClassName?: string;
  brandingSlug?: string | null;
};

const BrandLogo = ({
  showName = true,
  showSlogan = false,
  stacked = false,
  className,
  imageClassName,
  textClassName,
  brandingSlug,
}: BrandLogoProps) => {
  const { resolvedBrand } = useBranding({ slug: brandingSlug ?? undefined });
  const brand = resolvedBrand ?? baseBrand;
  const { resolvedTheme, theme } = useTheme();
  const activeTheme = resolvedTheme ?? theme;
  const prefersDark = activeTheme === "dark";

  // Respect the org-level hide_brand_name setting
  const effectiveShowName = showName && !brand.hideBrandName;

  const logoSrc = prefersDark
    ? brand.logo.light ?? brand.logo.dark ?? brand.logo.square
    : brand.logo.dark ?? brand.logo.light ?? brand.logo.square;

  if (logoSrc) {
    return (
      <div
        className={cn(
          "flex items-center gap-2",
          stacked && "flex-col items-center gap-1 text-center",
          className
        )}
      >
        <img
          src={logoSrc}
          alt={`${brand.name} logo`}
          className={cn("h-10 w-auto max-w-[12rem] object-contain", imageClassName)}
          decoding="async"
        />
        {(effectiveShowName || showSlogan) && (
          <div
            className={cn(
              "flex flex-col",
              stacked ? "items-center text-center" : "items-start",
              textClassName
            )}
          >
            {effectiveShowName && (
              <span className="font-semibold text-xl tracking-tight">
                {brand.name}
              </span>
            )}
            {showSlogan && brand.slogan && (
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-[0.2em]">
                {brand.slogan}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center font-black tracking-wide text-xl",
        className
      )}
    >
      {brand.shortName}
    </div>
  );
};

export default BrandLogo;
